<template>
  <div class="image-uploader-dialog">
    <vs-prompt
      id="image-uploader-dialog"
      @accept="onAccept"
      @cancel="onCancel"
      @close="onClose"
      :color="AISEA_COLOR.BLUE"
      :active="active"
      :title="title"
      :accept-text="acceptText"
      :cancel-text="cancelText"
      :is-valid="validateForm"
      :buttons-hidden="buttonsHidden"
    >
      <div>
        <p>ファイルの選択</p>
        <!-- ファイルを選択するまでの間表示 -->
        <div
          v-if="!hasDocuments"
          :class="[isDrag ? dragoverClass : dragleaveClass]"
          @dragover.prevent="checkDrag($event, true)"
          @dragleave.prevent="checkDrag($event, false)"
          @mouseleave.prevent="checkDrag($event, false)"
          @drop.prevent="handleFileSelect($event)"
          @click="$refs.update_photo_input.click()"
        >
          <input
            type="file"
            ref="update_photo_input"
            class="hidden"
            @change="handleFileSelect($event)"
          />
          <span
            >ファイルをドロップ、<br />またはクリックしてファイルを選択<br />
            <p class="text-sm mt-2">
              フォーマット: JPEG、JPG、PNG, HEIC
            </p>
            <p class="text-sm mt-2">
              最大アップロードサイズ：<strong>50MB</strong>
            </p>
          </span>
        </div>
        <!-- ファイルを選択したら表示 -->
        <div v-else>
          <div
            v-for="(file, index) in documents"
            :key="index"
            class="file-name-label relative p-2"
          >
            <!-- 書類名ラベルエリア -->
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="break-all">
                  {{ file.name }}
                </div>
              </div>

              <!-- 削除ボタン表示エリア -->
              <div class="hover-area">
                <div class="absolute top-0 right-0">
                  <div class="flex items-center mr-2">
                    <!-- 削除ボタン -->
                    <vx-tooltip text="削除">
                      <feather-icon
                        class="action-button-icon"
                        icon="TrashIcon"
                        svgClasses="w-5 h-5"
                        @click="deleteFile(file)"
                      ></feather-icon>
                    </vx-tooltip>
                  </div>
                </div>
              </div>
            </div>

            <!-- エラーの場合はエラーメッセージを表示 -->
            <div
              class="text-danger text-sm ml-8"
              v-for="error in errorsToBeDisplay"
              :key="error.id"
            >
              {{ error.msg }}
            </div>
          </div>
        </div>
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import { ColorMixin } from "@/mixins";
import _ from "lodash";

const MAXIMUM_FILE_SIZE = 50 * 1024 * 1024; //50MB

const EVENTS = {
  ACCEPT: "accept",
  CLOSE: "close",
  CANCEL: "cancel",
};

export default {
  name: "ImageUploadDialog",
  mixins: [ColorMixin],
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      title: "画像の追加",
      acceptText: "追加",
      cancelText: "キャンセル",
      buttonsHidden: false,
      dragleaveClass: "drag-area-dragleave",
      dragoverClass: "drag-area-dragover",
      documents: [],

      // ファイル選択エリア
      isDrag: false,
    };
  },
  computed: {
    validateForm() {
      return this.documents.length > 0 && !this.errors.any();
    },
    hasDocuments() {
      return this.documents.length > 0;
    },

    hasEditingFile() {
      return this.documents.filter(file => file.isEditing).length > 0;
    },

    editingFile() {
      return this.documents.find(file => file.isEditing);
    },
    errorsToBeDisplay() {
      const fields = new Set(
        this.documents.map(file => [file.field, `${file.field}_format`]).flat()
      );
      return this.$validator.errors.items.filter(error =>
        fields.has(error.field)
      );
    },
  },
  watch: {},
  methods: {
    validateFileType(file) {
      const fieldName = `${file.field}_format`;
      const validTypes = ["image/jpeg", "image/png", "image/heic"];
      this.$validator.errors.remove(fieldName);

      if (!validTypes.includes(file.type)) {
        this.$validator.errors.add({
          field: fieldName,
          msg: `選択できないフォーマットです`,
        });
      }
    },

    validateFileSize(file) {
      this.$validator.errors.remove(file.field);
      if (file.size > MAXIMUM_FILE_SIZE) {
        this.$validator.errors.add({
          field: file.field,
          msg: `最大アップロードサイズ50MBを超えてます`,
        });
      }
    },

    async onAccept() {
      this.$emit(EVENTS.ACCEPT, this.documents);
    },

    checkDrag(_e, isDrag) {
      this.isDrag = isDrag;
    },

    // 取り込みの参考
    handleFileSelect(e) {
      let files = [];

      if ("files" in e.target) {
        files = e.target.files;
      } else if ("files" in e.dataTransfer) {
        files = e.dataTransfer.files;
      }

      files.forEach(item => {
        const file = {};

        file.name = item.name;
        file.size = item.size;
        file.content = item; // アップロード用

        const heicExtensionRegex = /\.heic$/i;
        if (item.type != "image/heic" && heicExtensionRegex.test(item.name)) {
          file.type = "image/heic";
        } else {
          file.type = item.type;
        }

        file.field = `filename_${item.name}`;

        const reader = new FileReader();
        reader.onload = _e => {
          file.src = _e.target.result;
        };
        reader.readAsDataURL(item);

        this.validateFileType(file);
        this.validateFileSize(file);

        this.documents.push(file);
      });
    },

    deleteFile(file) {
      this.$validator.errors.remove(file.field);
      this.$validator.errors.remove(`${file.field}_format`);
      this.documents = this.documents.filter(item => {
        return file !== item;
      });
    },

    onClose() {
      this.$emit(EVENTS.CLOSE);
      setTimeout(() => {
        this.cleanUp();
      }, 100);
    },

    onCancel() {
      this.$emit(EVENTS.CANCEL);
      setTimeout(() => {
        this.cleanUp();
      }, 100);
    },

    removeAllErrors() {
      this.documents.forEach(file => {
        this.$validator.errors.remove(file.field);
        this.$validator.errors.remove(`${file.field}_format`);
      });
    },

    removeTooltip() {
      //ツールチップが消えていない場合、消す。
      document.querySelectorAll("body > div.vs-tooltip").forEach(el => {
        if (document.body.contains(el)) {
          document.body.removeChild(el);
        }
      });
    },

    cleanUp() {
      this.removeTooltip();
      this.removeAllErrors();
      this.$validator.pause();
      this.documents = [];
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

.vs-tooltip {
  z-index: 52006;
}

#image-uploader-dialog {
  .scroll-area {
    max-height: 360px;
  }

  .file-name-label {
    .action-button-icon {
      border-radius: 4px;
      padding: 8px;
      margin: 0;
      margin-left: 6px;
      color: $aisea-gray;
      background-color: $aisea-white;
      cursor: pointer;

      &__disabled {
        color: $aisea-light-gray;
        background-color: $aisea-white;
        cursor: default;
      }
    }
  }

  .drag-area-dragleave {
    width: 100%;
    min-width: 200px;
    height: 100%;
    min-height: 200px;
    border: 2px dotted rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    background-color: #f5f5f5;
    text-align: center;
  }
  .drag-area-dragover {
    width: 100%;
    min-width: 200px;
    height: 100%;
    min-height: 200px;
    border: 2px solid $aisea-blue;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    background-color: #f5f5f5;
    text-align: center;
  }
  .vs-dialog {
    .vs-dialog-cancel-button {
      background: #f8f8f8 !important;
      .vs-button-backgroundx.vs-button--background {
        display: none;
      }
    }
  }
}
</style>
